import React from 'react';
import PropTypes from 'prop-types';
import head from 'lodash.head';
import isEmpty from 'lodash.isempty';
import ReactMarkdown from 'react-markdown';
import { buildProfileDescription } from '../../../../utils/shared/instructionUtils';
import GroupHeader from './GroupHeader';
import Section from '../Section';
import TabsSection from '../TabsSection';
import ExpandCollapsWrapper from '../../../../components/expandCollapsWrapper/ExpandCollapsWrapper';
import ObservationSection from '../observations/ObservationSection';
import { generateReactKey } from '../../../../utils/shared/commonUtils';
import { formLrsGroupName } from '../../../lrs/shared/lrsHelpers';
import { ACTIVITIES_SECTION } from '../../../../constants/reading/DemoModeConstants';
import './Group.scss';

const DescriptionSection = ({ description }) => (
    <div className="desc border-top">
        <Section
            className="can-section"
            items={Array.of(head(buildProfileDescription(description)))}
        />
        <Section
            className="need-section"
            items={Array.of(buildProfileDescription(description).pop())}
        />
    </div>
);

const CollapsedSection = ({
    text,
    observations,
    activities,
    content,
    activitiesData,
    measureList,
    observationActivitiesMapper,
    selectedGrade,
    widget,
    staffId,
    lrsTrackingInfo,
    children,
    groupName,
    selectObservation,
    selectPage,
    lrsLanguageConsiderationPdf,
    sessionId,
    lastLocation,
    switchLanguageConsiderationsTabLrs,
    lrsGroup,
}) => {
    const { tips, skills, notes, ell, gel } = text;
    const tabs = [];

    if (!isEmpty(ell)) {
        tabs.push({
            className: 'ell',
            itemClassName: 'section',
            items: ell,
            header: 'english language learner',
        });
    }

    if (!isEmpty(gel)) {
        tabs.push({
            className: 'gel',
            itemClassName: 'section',
            items: gel,
            header: 'language variety',
        });
    }

    return (
        <>
            <div className="collapsed-section border-top" data-tour-step={ACTIVITIES_SECTION}>
                <div className="tips-and-skills">
                    <Section
                        className="tips"
                        itemClassName="tip"
                        items={tips.map((item, itemIndex) => (
                            <ReactMarkdown key={generateReactKey(itemIndex)}>{item}</ReactMarkdown>
                        ))}
                    />
                    <Section
                        className="skills"
                        items={skills}
                        header={
                            <div className="font-weight-bold font-italic">Prerequisite skills:</div>
                        }
                    />
                    <Section items={notes} className="notes" itemClassName="font-italic" />
                </div>

                {!isEmpty(tabs) && (
                    <TabsSection
                        title="Language Considerations"
                        infoText="To better understand students’ instructional needs, we recommend taking their language backgrounds into consideration. This includes students who may speak other languages (e.g., English language learners) as well as those who may speak other language varieties (i.e., dialects)."
                        tabs={tabs}
                        lrsLanguageConsiderationPdf={lrsLanguageConsiderationPdf}
                        switchLanguageConsiderationsTabLrs={switchLanguageConsiderationsTabLrs}
                        lrsGroup={lrsGroup}
                    />
                )}
            </div>
            <ObservationSection
                groupName={groupName}
                content={content}
                observations={observations}
                staticActivitiesIds={activities}
                hideObservationsIfNoneExists
                className="groups-observations"
                measureList={measureList}
                observationActivitiesMapper={observationActivitiesMapper}
                selectedGrade={selectedGrade}
                staffId={staffId}
                lrsTrackingInfo={lrsTrackingInfo}
                activitiesData={activitiesData}
                selectObservation={selectObservation}
                selectPage={selectPage}
                sessionId={sessionId}
                lastLocation={lastLocation}
                lrsGroup={lrsGroup}
            >
                {children}
            </ObservationSection>
            {widget}
        </>
    );
};

const Group = ({
    groupId,
    name,
    skill,
    students,
    text,
    observations,
    activities,
    content,
    activitiesData,
    measureList,
    observationActivitiesMapper,
    selectedGrade,
    widget,
    staffId,
    lrsTrackingInfo,
    bulkActivitiesWidgetProps,
    bulkActivitiesWidget: BulkActivitiesWidget,
    seeActivitiesToggle,
    selectObservation,
    selectPage,
    lrsLanguageConsiderationPdf,
    switchLanguageConsiderationsTabLrs,
    sessionId,
    lastLocation,
    isGuidedTourOn,
}) => {
    const lrsGroup = formLrsGroupName(name, groupId);
    const handleBulkActivityClick = location => () => {
        bulkActivitiesWidgetProps.onClick(location, activities, lrsGroup);
    };

    return (
        <div
            data-tour-step={`group-${groupId}`}
            className={`${isGuidedTourOn ? 'in-tour' : ''} student-group`}
        >
            <div className="bulk-activities-header">
                <div className="skill font-weight-bold">{skill.toUpperCase()}</div>
                <BulkActivitiesWidget
                    {...bulkActivitiesWidgetProps}
                    onClick={handleBulkActivityClick('all-above-group')}
                />
            </div>

            <ExpandCollapsWrapper
                groupId={groupId}
                moreButtonText="See activities"
                toggleHandler={seeActivitiesToggle}
                lrsGroup={lrsGroup}
            >
                <>
                    <GroupHeader id={groupId} name={name} count={students.length} />
                    <Section
                        className="students-section"
                        itemClassName="student-name"
                        items={students.map(
                            student => `${student.firstName} ${student.lastName.charAt(0)}`,
                        )}
                    />
                    <DescriptionSection description={text.desc} />
                </>
                <CollapsedSection
                    groupName={name}
                    text={text}
                    observations={observations}
                    activities={activities}
                    content={content}
                    activitiesData={activitiesData}
                    measureList={measureList}
                    observationActivitiesMapper={observationActivitiesMapper}
                    selectedGrade={selectedGrade}
                    widget={widget}
                    staffId={staffId}
                    lrsTrackingInfo={lrsTrackingInfo}
                    selectObservation={selectObservation}
                    selectPage={selectPage}
                    lrsLanguageConsiderationPdf={lrsLanguageConsiderationPdf}
                    switchLanguageConsiderationsTabLrs={switchLanguageConsiderationsTabLrs}
                    sessionId={sessionId}
                    lastLocation={lastLocation}
                    lrsGroup={lrsGroup}
                >
                    <BulkActivitiesWidget
                        {...bulkActivitiesWidgetProps}
                        onClick={handleBulkActivityClick('all-below-patterns')}
                    />
                </CollapsedSection>
            </ExpandCollapsWrapper>
        </div>
    );
};

DescriptionSection.defaultProps = {
    description: [],
};

DescriptionSection.propTypes = {
    description: PropTypes.array,
};

CollapsedSection.defaultProps = {
    text: {},
    observations: [],
    activities: [],
    measureList: [],
    content: [],
    activitiesData: [],
    staffId: null,
    lrsTrackingInfo: {},
    children: {},
    observationActivitiesMapper: {},
    selectedGrade: null,
    widget: <></>,
    groupName: null,
    selectObservation: () => {},
    selectPage: () => {},
    lrsLanguageConsiderationPdf: () => {},
    switchLanguageConsiderationsTabLrs: () => {},
    sessionId: '',
    lastLocation: {},
    lrsGroup: '',
};

CollapsedSection.propTypes = {
    text: PropTypes.object,
    observations: PropTypes.array,
    activities: PropTypes.array,
    measureList: PropTypes.array,
    content: PropTypes.array,
    activitiesData: PropTypes.array,
    staffId: PropTypes.string,
    lrsTrackingInfo: PropTypes.object,
    children: PropTypes.object,
    observationActivitiesMapper: PropTypes.object,
    selectedGrade: PropTypes.string,
    widget: PropTypes.element,
    groupName: PropTypes.string,
    selectObservation: PropTypes.func,
    selectPage: PropTypes.func,
    lrsLanguageConsiderationPdf: PropTypes.func,
    switchLanguageConsiderationsTabLrs: PropTypes.func,
    sessionId: PropTypes.string,
    lastLocation: PropTypes.object,
    lrsGroup: PropTypes.string,
};

Group.defaultProps = {
    groupId: 0,
    name: '',
    skill: '',
    students: [],
    text: {},
    observations: [],
    activities: [],
    content: [],
    activitiesData: [],
    measureList: [],
    observationActivitiesMapper: {},
    selectedGrade: null,
    widget: <></>,
    staffId: null,
    lrsTrackingInfo: {},
    bulkActivitiesWidget: () => {},
    seeActivitiesToggle: () => {},
    selectObservation: () => {},
    selectPage: () => {},
    bulkActivitiesWidgetProps: { onClick: () => {} },
    lrsLanguageConsiderationPdf: () => {},
    switchLanguageConsiderationsTabLrs: () => {},
    sessionId: '',
    lastLocation: {},
    isGuidedTourOn: false,
};

Group.propTypes = {
    groupId: PropTypes.number,
    name: PropTypes.string,
    skill: PropTypes.string,
    students: PropTypes.array,
    text: PropTypes.object,
    observations: PropTypes.array,
    activities: PropTypes.array,
    content: PropTypes.array,
    activitiesData: PropTypes.array,
    measureList: PropTypes.array,
    observationActivitiesMapper: PropTypes.object,
    selectedGrade: PropTypes.string,
    widget: PropTypes.element,
    staffId: PropTypes.string,
    lrsTrackingInfo: PropTypes.object,
    bulkActivitiesWidgetProps: PropTypes.object,
    bulkActivitiesWidget: PropTypes.func,
    seeActivitiesToggle: PropTypes.func,
    selectObservation: PropTypes.func,
    selectPage: PropTypes.func,
    lrsLanguageConsiderationPdf: PropTypes.func,
    switchLanguageConsiderationsTabLrs: PropTypes.func,
    sessionId: PropTypes.string,
    lastLocation: PropTypes.object,
    isGuidedTourOn: PropTypes.bool,
};

export default Group;
